import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Lock, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirm) return;
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/auth/password/reset', {
        token,
        new_password: password,
      });
      const { access_token, user } = res.data;
      await login(access_token, user);
      setDone(true);
      setTimeout(() => {
        navigate((user.account_type === 'staff' || user.role === 'admin') ? '/team' : '/account', { replace: true });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
        <div className="text-center">
          <Shield className="h-8 w-8 mx-auto mb-3 text-ink" strokeWidth={1.5} />
          <h1 className="font-display text-xl font-medium text-ink mb-2">Invalid Link</h1>
          <p className="text-sm text-muted-foreground mb-4">No reset token found in the URL.</p>
          <Link to="/account/forgot-password">
            <Button variant="outline" size="sm">Request a New Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm px-4"
      >
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 no-underline mb-6">
            <Shield className="h-6 w-6 text-ink" strokeWidth={1.5} />
            <span className="font-display text-lg font-medium text-ink">The Fashion Detective</span>
          </Link>
        </div>

        <Card className="bg-white border-stone/50 shadow-[var(--shadow-card)]">
          <CardContent className="p-6">
            {done ? (
              <div className="text-center py-4" data-testid="reset-success">
                <CheckCircle className="h-10 w-10 mx-auto mb-3" style={{ color: '#0E6B5D' }} />
                <h2 className="font-display text-lg font-medium text-ink mb-2">Password Reset</h2>
                <p className="text-sm text-muted-foreground">Your password has been set. Redirecting...</p>
              </div>
            ) : (
              <>
                <h1 className="font-display text-xl font-medium text-ink mb-1">Set New Password</h1>
                <p className="text-xs text-muted-foreground mb-5">
                  Choose a strong password for your account.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                  <div>
                    <Label htmlFor="new-password" className="text-xs">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      className="mt-1"
                      data-testid="reset-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-xs">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Repeat your password"
                      required
                      className="mt-1"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription className="text-xs">{error}</AlertDescription>
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    className="w-full gap-2 rounded-[12px] h-11 active:scale-[0.98]"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    {loading ? 'Resetting...' : 'Set New Password'}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Link to="/account/sign-in" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink">
            <ArrowLeft className="h-3 w-3" /> Back to Sign In
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
